<div class="card">
  <div class="card-header card-header-danger">
    <h4 class="card-title">Delivery Information</h4>
  </div>
  <div class="card-body">
    <!-- Transhipper markups are the same for every vendor in a market, so they
         live on the Transhipment page rather than here. -->
    <p class="text-muted">
      Transhipper markups are set per market on the
      <a routerLink="/transhipment">Transhipment Default</a> page.
    </p>
    @if (isNewVendor) {
      <p class="text-muted">
        A new vendor starts with these delivery fees: $10 in SG and $50 in every US zone. Create
        the vendor first, then change them here.
      </p>
    }
    <table mat-table [dataSource]="deliveryRows()" class="w-100">
      <!-- Country Column -->
      <ng-container matColumnDef="iso2">
        <th mat-header-cell *matHeaderCellDef>ISO2</th>
        <!-- Ticked: the vendor may list products in this market. Unticked, the
             merchant portal greys the market out. Saved as soon as it is clicked. -->
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            color="primary"
            [checked]="allowedMarkets().includes(row.iso2)"
            [disabled]="isNewVendor || savingMarkets().has(row.iso2)"
            (change)="setMarketAllowed(row.iso2, $event.checked)"
            [matTooltip]="isNewVendor ? 'Create the vendor first' : 'Allow this vendor to sell in ' + row.iso2"
          >
            {{ row.iso2 }}
          </mat-checkbox>
        </td>
      </ng-container>

      <!-- Delivered By: Merchant Self Delivery shows the delivery fees on the
           merchant's order email and portal orders; FishyHub Delivery (the
           default) does not. -->
      <ng-container matColumnDef="deliveryMode">
        <th mat-header-cell *matHeaderCellDef>Delivered By</th>
        <td mat-cell *matCellDef="let row">
          <mat-button-toggle-group
            class="delivery-mode"
            [formControl]="row.formGroup.get('deliveryMode')"
            aria-label="Delivered by"
            [vertical]="true"
          >
            <mat-button-toggle value="fishyhub">Fishyhub Delivery</mat-button-toggle>
            <mat-button-toggle value="merchant">Merchant Self Delivery</mat-button-toggle>
          </mat-button-toggle-group>
        </td>
      </ng-container>

      <!-- Delivery Text Column -->
      <ng-container matColumnDef="deliveryText">
        <th mat-header-cell *matHeaderCellDef>Delivery Text</th>
        <td mat-cell *matCellDef="let row">
          <mat-form-field class="w-100">
            <textarea matInput [formControl]="row.formGroup.get('deliveryText')" rows="3"></textarea>
            @if (row.formGroup.get('deliveryText')?.errors?.['required']) {
              <mat-error>Delivery text is required</mat-error>
            }
          </mat-form-field>
        </td>
      </ng-container>

      <!-- Standard Delivery Fee Column -->
      <ng-container matColumnDef="standardDeliveryFee">
        <th mat-header-cell *matHeaderCellDef>Standard Delivery Fee</th>
        <td mat-cell *matCellDef="let row">
          @if (deliveryZonesFor(row.iso2).length > 0) {
            <!-- US / MY: charged by the customer's shipping-address state, per zone. -->
            <div class="zone-fees">
              @for (z of deliveryZonesFor(row.iso2); track z.zone) {
                <mat-form-field subscriptSizing="dynamic">
                  <mat-label>{{ z.label }}</mat-label>
                  <input matInput type="number" [formControl]="row.formGroup.get(zoneControlName(z.zone))" />
                  <mat-hint>{{ z.states }}</mat-hint>
                  @if (row.formGroup.get(zoneControlName(z.zone))?.errors?.['required']) {
                    <mat-error>Required</mat-error>
                  }
                  @if (row.formGroup.get(zoneControlName(z.zone))?.errors?.['min']) {
                    <mat-error>Cannot be negative</mat-error>
                  }
                </mat-form-field>
              }
            </div>
          } @else {
            <mat-form-field>
              <input matInput type="number" [formControl]="row.formGroup.get('standardDeliveryFee')" />
              @if (row.formGroup.get('standardDeliveryFee')?.errors?.['required']) {
                <mat-error>Required</mat-error>
              }
            </mat-form-field>
          }
        </td>
      </ng-container>

      <!-- Walk-in Column: free text because collection is the vendor's own
           arrangement - where the shop is, when it opens, what to bring. Blank
           is how a vendor says they do not offer it. -->
      <ng-container matColumnDef="walkInText">
        <th mat-header-cell *matHeaderCellDef>Walk in</th>
        <td mat-cell *matCellDef="let row">
          <mat-form-field>
            <textarea
              matInput
              [formControl]="row.formGroup.get('walkInText')"
              rows="3"
              placeholder="e.g. Collect at 10 Fishery Rd, Mon-Sat 10am-7pm"
            ></textarea>
            <mat-hint>Leave blank if walk-in is not offered.</mat-hint>
          </mat-form-field>
        </td>
      </ng-container>

      <!-- Minimum Subtotal Value Column -->
      <ng-container matColumnDef="minSubtotal">
        <th mat-header-cell *matHeaderCellDef>Minimum Subtotal Value</th>
        <td mat-cell *matCellDef="let row">
          <mat-form-field>
            <mat-label>Enabled</mat-label>
            <mat-select [formControl]="row.formGroup.get('minSubtotalEnabled')">
              <mat-option [value]="false">No</mat-option>
              <mat-option [value]="true">Yes</mat-option>
            </mat-select>
          </mat-form-field>
          @if (row.formGroup.get('minSubtotalEnabled')?.value) {
            <mat-form-field>
              <mat-label>Min. Subtotal</mat-label>
              <input matInput type="number" [formControl]="row.formGroup.get('minSubtotalValue')" />
              @if (row.formGroup.get('minSubtotalValue')?.errors?.['required']) {
                <mat-error>Required</mat-error>
              }
              @if (row.formGroup.get('minSubtotalValue')?.errors?.['min']) {
                <mat-error>Must be greater than 0</mat-error>
              }
            </mat-form-field>
          }
        </td>
      </ng-container>

      <!-- Actions Column -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Actions</th>
        <td mat-cell *matCellDef="let row">
          <button 
            mat-raised-button 
            color="primary" 
            (click)="saveVendorDeliveryInfo(row.iso2)"
            [disabled]="row.formGroup.invalid || isNewVendor"
          >
            Save
          </button>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
    </table>

    @if (success()) {
      <div class="alert alert-success mt-3">{{ success() }}</div>
    }
    @if (error()) {
      <div class="alert alert-danger mt-3">{{ error() }}</div>
    }
  </div>
</div>